import { useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  sendEmailVerification,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getClientAuth, getClientDB } from '@canvas/firebase-config/client';
import { userPath } from '@canvas/shared-types';
import { isValidEmail } from '@canvas/utils';

export interface AuthUser {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  emailVerified: boolean;
}

function path(segments: string) {
  return segments.replace(/^\/+/, '');
}

function authErrorMessage(err: unknown, fallback: string): string {
  const code =
    err && typeof err === 'object' && 'code' in err
      ? String((err as { code?: string }).code)
      : '';
  const message = err instanceof Error ? err.message : '';

  switch (code) {
    case 'auth/operation-not-allowed':
      return 'Email/password sign-in is disabled for this Firebase project. In Firebase Console → Authentication → Sign-in method, enable Email/Password (and Google if you use it).';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Sign in instead.';
    case 'auth/invalid-email':
      return 'Enter a valid email address.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a moment and try again.';
    case 'auth/popup-closed-by-user':
      return 'Google sign-in was cancelled.';
    case 'auth/unauthorized-domain':
      return 'This domain is not authorized for Firebase Auth. Add it under Authentication → Settings → Authorized domains.';
    default:
      return message || fallback;
  }
}

async function ensureUserDocument(firebaseUser: FirebaseUser) {
  const db = getClientDB();
  const ref = doc(db, path(userPath(firebaseUser.uid)));
  const snap = await getDoc(ref);

  if (snap.exists()) {
    await setDoc(
      ref,
      {
        lastLoginAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        email: firebaseUser.email || '',
        displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
        avatarUrl: firebaseUser.photoURL || null,
        emailVerified: firebaseUser.emailVerified,
      },
      { merge: true }
    );
    return;
  }

  await setDoc(ref, {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
    avatarUrl: firebaseUser.photoURL || null,
    plan: 'free', // TODO(subscription): keep free until billing ships
    exportsThisMonth: 0,
    aiUsageThisMonth: 0,
    totalProjects: 0,
    totalExports: 0,
    emailVerified: firebaseUser.emailVerified,
    isActive: true,
    preferences: {
      darkMode: 'system',
      defaultCanvasSize: { width: 1080, height: 1080 },
      autoSave: true,
      autoSaveInterval: 10,
      showGrid: false,
      gridSize: 20,
      measurementUnit: 'px',
      snapGuides: true,
      showRulers: false,
      defaultExportFormat: 'png',
    },
    planInfo: {
      plan: 'free',
      expiresAt: null,
      isActive: true,
      autoRenew: false,
      features: {
        // Free preview — effectively unlimited until subscriptions launch
        maxProjects: 9999,
        maxExportsPerMonth: 9999,
        maxAIUsagePerMonth: 9999,
        maxTeamMembers: 1,
        customDomain: false,
        apiAccess: false,
        prioritySupport: false,
      },
    },
    createdAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

function toAuthUser(user: FirebaseUser): AuthUser {
  return {
    uid: user.uid,
    email: user.email || '',
    displayName: user.displayName || user.email?.split('@')[0] || 'User',
    photoURL: user.photoURL || undefined,
    emailVerified: user.emailVerified,
  };
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const auth = getClientAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          await ensureUserDocument(firebaseUser);
          setUser(toAuthUser(firebaseUser));
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Auth profile sync failed:', err);
        if (firebaseUser) {
          setUser(toAuthUser(firebaseUser));
        }
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const clearError = () => setError(null);

  const signInEmail = async (email: string, password: string) => {
    setError(null);
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return false;
    }

    try {
      await signInWithEmailAndPassword(getClientAuth(), email.trim(), password);
      return true;
    } catch (err) {
      setError(authErrorMessage(err, 'Sign in failed.'));
      return false;
    }
  };

  const signUpEmail = async (name: string, email: string, password: string) => {
    setError(null);
    if (!name.trim()) {
      setError('Please enter your name.');
      return false;
    }
    if (!isValidEmail(email)) {
      setError('Enter a valid email address.');
      return false;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return false;
    }

    try {
      const cred = await createUserWithEmailAndPassword(
        getClientAuth(),
        email.trim(),
        password
      );
      await updateProfile(cred.user, { displayName: name.trim() });
      try {
        await sendEmailVerification(cred.user);
      } catch (verifyErr) {
        console.warn('Failed to send verification email:', verifyErr);
      }
      await ensureUserDocument(cred.user);
      setUser(
        toAuthUser({
          ...cred.user,
          displayName: name.trim(),
          emailVerified: cred.user.emailVerified,
        } as FirebaseUser)
      );
      return true;
    } catch (err) {
      setError(authErrorMessage(err, 'Sign up failed.'));
      return false;
    }
  };

  const signInGoogle = async () => {
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(getClientAuth(), provider);
      return true;
    } catch (err) {
      setError(authErrorMessage(err, 'Google sign-in failed.'));
      return false;
    }
  };

  const resendVerificationEmail = async (): Promise<boolean> => {
    setError(null);
    const current = getClientAuth().currentUser;
    if (!current) {
      setError('Sign in to resend the verification email.');
      return false;
    }
    if (current.emailVerified) {
      setUser(toAuthUser(current));
      return true;
    }
    try {
      await sendEmailVerification(current);
      return true;
    } catch (err) {
      setError(authErrorMessage(err, 'Could not resend verification email.'));
      return false;
    }
  };

  const refreshEmailVerified = async (): Promise<boolean> => {
    setError(null);
    const current = getClientAuth().currentUser;
    if (!current) return false;
    try {
      await current.reload();
      const refreshed = getClientAuth().currentUser;
      if (!refreshed) return false;
      await ensureUserDocument(refreshed);
      setUser(toAuthUser(refreshed));
      return refreshed.emailVerified;
    } catch (err) {
      setError(authErrorMessage(err, 'Could not refresh verification status.'));
      return false;
    }
  };

  const logout = async () => {
    setError(null);
    try {
      sessionStorage.removeItem('craftara_verify_banner_dismissed');
    } catch {
      /* ignore */
    }
    await signOut(getClientAuth());
  };

  return {
    user,
    loading,
    error,
    clearError,
    signInEmail,
    signUpEmail,
    signInGoogle,
    resendVerificationEmail,
    refreshEmailVerified,
    logout,
  };
}
